import { CheckCircle2, Lightbulb, ChevronDown, ChevronUp, Square, SquareCheckBig, PlayCircle, GraduationCap } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export const StepChecklist = ({ instructions, parentTips, completedSteps, expandedTips, onToggleStep, onToggleTip }) => {
  const completedCount = Object.values(completedSteps).filter(Boolean).length;

  return (
    <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="instructions-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl text-secondary">Activity Steps</CardTitle>
            <CardDescription>Tick off each step as your child completes it</CardDescription>
          </div>
          {instructions.length > 0 && (
            <span className="text-sm font-bold text-primary bg-primary/10 px-3 py-1.5 rounded-full">
              {completedCount}/{instructions.length}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <ol className="space-y-4">
          {instructions.map((instruction, index) => {
            const isDone = !!completedSteps[index];
            const hasTip = parentTips && parentTips[index];
            const tipExpanded = !!expandedTips[index];

            return (
              <li key={`step-${index}-${instruction.slice(0, 20)}`} data-testid={`step-${index}`} className={`rounded-2xl border-2 transition-all duration-300 ${isDone ? 'border-green-300 bg-green-50/50' : 'border-border/50 bg-background'}`}>
                <div className="flex gap-3 p-4">
                  <button
                    type="button"
                    data-testid={`step-check-${index}`}
                    onClick={() => onToggleStep(index)}
                    className="flex-shrink-0 mt-0.5 transition-transform hover:scale-110"
                    aria-label={`Mark step ${index + 1} as ${isDone ? 'incomplete' : 'complete'}`}
                  >
                    {isDone ? (
                      <SquareCheckBig className="h-6 w-6 text-green-600" />
                    ) : (
                      <Square className="h-6 w-6 text-foreground/30 hover:text-primary" />
                    )}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${isDone ? 'bg-green-600 text-white' : 'bg-primary text-white'}`}>
                        {index + 1}
                      </span>
                      <span className={`flex-1 pt-0.5 ${isDone ? 'text-foreground/50 line-through' : 'text-foreground/80'}`}>
                        {instruction}
                      </span>
                    </div>

                    {hasTip && (
                      <div className="mt-3 ml-10">
                        <button
                          type="button"
                          data-testid={`tip-toggle-${index}`}
                          onClick={() => onToggleTip(index)}
                          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                        >
                          <Lightbulb className="h-4 w-4" />
                          Parent Tip
                          {tipExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        </button>
                        {tipExpanded && (
                          <div className="mt-2 p-3 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-800 leading-relaxed animate-in slide-in-from-top-1 duration-200">
                            {parentTips[index]}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
};

export const OutcomeChecklist = ({ outcomeAnalysis, learningOutcomes, achievedOutcomes, onToggleOutcome }) => {
  // Use outcome_analysis if available, otherwise fall back to learning_outcomes
  if (outcomeAnalysis?.length > 0) {
    const completedCount = outcomeAnalysis.filter(item => achievedOutcomes[item.outcome]).length;

    return (
      <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="outcome-checklist-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-secondary">Did Your Child Achieve These?</CardTitle>
              <CardDescription>Tick off each outcome as you observe your child demonstrating it</CardDescription>
            </div>
            <span className="text-sm font-bold text-primary bg-primary/10 px-3 py-1.5 rounded-full">
              {completedCount}/{outcomeAnalysis.length}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {outcomeAnalysis.map((item, index) => {
              const isDone = !!achievedOutcomes[item.outcome];
              return (
                <div key={item.outcome} data-testid={`outcome-item-${index}`} className={`rounded-2xl border-2 transition-all duration-300 ${isDone ? 'border-green-300 bg-green-50/50' : 'border-border/50 bg-background'}`}>
                  <div className="p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <button
                        type="button"
                        data-testid={`outcome-check-${index}`}
                        onClick={() => onToggleOutcome(item.outcome)}
                        className="flex-shrink-0 mt-0.5 transition-transform hover:scale-110"
                      >
                        {isDone ? (
                          <SquareCheckBig className="h-6 w-6 text-green-600" />
                        ) : (
                          <Square className="h-6 w-6 text-foreground/30 hover:text-primary" />
                        )}
                      </button>
                      <h4 className={`font-semibold text-lg ${isDone ? 'text-green-700' : 'text-secondary'}`}>
                        {item.outcome}
                      </h4>
                    </div>

                    <div className="ml-9 space-y-3">
                      <div className="p-3 bg-muted/60 rounded-xl">
                        <p className="text-xs font-semibold text-foreground/50 uppercase mb-1">What success looks like:</p>
                        <p className="text-sm text-foreground/80">{item.criteria}</p>
                      </div>

                      {item.evidence_cues?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-foreground/50 uppercase mb-2">Signs to look for:</p>
                          <ul className="space-y-1.5">
                            {item.evidence_cues.map((cue) => (
                              <li key={cue} className="flex items-start gap-2 text-sm text-foreground/80">
                                <CheckCircle2 className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isDone ? 'text-green-500' : 'text-foreground/30'}`} />
                                {cue}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fallback: simple learning outcomes list
  if (learningOutcomes?.length > 0) {
    return (
      <Card className="rounded-3xl border-border/50 shadow-sm" data-testid="outcome-checklist-card">
        <CardHeader>
          <CardTitle className="text-2xl text-secondary">Did Your Child Achieve These?</CardTitle>
          <CardDescription>Tick off each learning outcome as you observe it</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {learningOutcomes.map((outcome) => {
              const isDone = !!achievedOutcomes[outcome];
              return (
                <div key={outcome} data-testid={`outcome-item-simple-${outcome.slice(0, 15)}`} className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-all ${isDone ? 'border-green-300 bg-green-50/50' : 'border-border/50'}`}>
                  <button type="button" onClick={() => onToggleOutcome(outcome)} className="flex-shrink-0 mt-0.5">
                    {isDone ? <SquareCheckBig className="h-5 w-5 text-green-600" /> : <Square className="h-5 w-5 text-foreground/30 hover:text-primary" />}
                  </button>
                  <span className={`${isDone ? 'text-foreground/50 line-through' : 'text-foreground/80'}`}>{outcome}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export const YouTubeReferences = ({ references }) => {
  if (!references?.length) return null;

  return (
    <Card className="rounded-3xl border-red-200/60 shadow-sm bg-gradient-to-br from-red-50/40 to-background" data-testid="youtube-references-card">
      <CardHeader>
        <CardTitle className="text-2xl text-secondary flex items-center gap-2">
          <svg className="h-6 w-6 text-red-500" viewBox="0 0 24 24" fill="currentColor"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0C.488 3.45.029 5.804 0 12c.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0C23.512 20.55 23.971 18.196 24 12c-.029-6.185-.484-8.549-4.385-8.816zM9 16V8l8 4-8 4z"/></svg>
          Helpful Videos for Parents
        </CardTitle>
        <CardDescription>Watch these to prepare yourself before guiding your child</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {references.map((ref) => (
            <a
              key={ref.search_query}
              data-testid={`youtube-ref-${ref.topic}`}
              href={`https://www.youtube.com/results?search_query=${encodeURIComponent(ref.search_query)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 p-4 rounded-2xl border border-border/50 hover:border-red-300 hover:bg-red-50/30 transition-all group"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center group-hover:bg-red-200 transition-colors">
                <svg className="h-5 w-5 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3" /></svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground group-hover:text-red-700 transition-colors">{ref.topic}</p>
                <p className="text-sm text-foreground/60 mt-0.5">{ref.why_watch || ref.description}</p>
              </div>
              <svg className="h-4 w-4 text-foreground/30 group-hover:text-red-500 flex-shrink-0 mt-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>
          ))}
        </div>
        <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
          <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 12 15 16 10" /></svg>
          <p className="text-xs text-amber-800">
            These links open YouTube search results. Please preview videos before sharing with your child to ensure the content is appropriate and age-suitable.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Section 21: Parent Prep videos (always visible - marketed feature)
export const ParentPrepVideos = ({ videos }) => {
  const items = Array.isArray(videos) ? videos.filter((v) => v && (v.title || v.search_query)) : [];

  return (
    <Card className="rounded-3xl border-primary/20 shadow-sm bg-gradient-to-br from-primary/5 to-background" data-testid="parent-prep-card">
      <CardHeader>
        <CardTitle className="text-2xl text-secondary flex items-center gap-2">
          <PlayCircle className="h-6 w-6 text-primary" />
          Parent Prep
        </CardTitle>
        <CardDescription>Watch a short video to feel ready before you start with your child</CardDescription>
      </CardHeader>
      <CardContent>
        {items.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {items.slice(0, 2).map((video, idx) => (
              <a
                key={idx}
                data-testid={`parent-prep-video-${idx}`}
                href={`https://www.youtube.com/results?search_query=${encodeURIComponent(video.search_query || video.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-2xl border border-border/50 overflow-hidden hover:border-primary/40 hover:shadow-md transition-all"
              >
                <div className="relative aspect-video bg-secondary flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary to-secondary/70" />
                  <div className="relative z-10 w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <PlayCircle className="h-8 w-8 text-primary" />
                  </div>
                  <span className="absolute bottom-2 right-2 z-10 text-[10px] font-medium text-white/90 bg-black/40 px-2 py-0.5 rounded-full">Prep video</span>
                </div>
                <div className="p-4">
                  <p className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">{video.title}</p>
                  {video.description && (
                    <p className="text-sm text-foreground/60 mt-1 line-clamp-2">{video.description}</p>
                  )}
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-dashed border-border p-8 text-center" data-testid="parent-prep-placeholder">
            <div className="w-14 h-14 rounded-full bg-muted mx-auto flex items-center justify-center mb-3">
              <PlayCircle className="h-8 w-8 text-foreground/40" />
            </div>
            <p className="font-semibold text-secondary">Prep video coming soon</p>
            <p className="text-sm text-foreground/60 mt-1">We're creating a short video to help you run this activity with confidence.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Section 20: Curricular Alignment in parent-friendly language (hidden only if no data)
export const CurricularAlignment = ({ alignment, curricularAreas, learningOutcomes }) => {
  const a = alignment || {};
  const ncf = curricularAreas?.ncf_se_2023 || [];
  const outcomes = (a.learning_outcomes && a.learning_outcomes.length)
    ? a.learning_outcomes
    : (learningOutcomes || []);
  const hasNios = !!(a.nios_mapping && a.nios_mapping.trim());

  const hasAnything =
    a.grade_band || a.subject_area || outcomes.length || hasNios ||
    (a.nep_2020_competency && a.nep_2020_competency.trim()) || ncf.length;

  if (!hasAnything) return null;

  return (
    <Card className="rounded-3xl border-secondary/20 shadow-sm bg-gradient-to-br from-secondary/5 to-background" data-testid="curricular-areas-card">
      <CardHeader>
        <CardTitle className="text-2xl text-secondary flex items-center gap-2">
          <GraduationCap className="h-6 w-6 text-secondary" />
          Curricular Alignment
        </CardTitle>
        <CardDescription>How this activity connects to your child's curriculum</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-3">
          {a.grade_band && (
            <div className="px-4 py-2 rounded-2xl bg-secondary/10" data-testid="alignment-grade-band">
              <p className="text-xs text-foreground/50 font-medium">Stage</p>
              <p className="text-sm font-semibold text-secondary">{a.grade_band}</p>
            </div>
          )}
          {a.subject_area && (
            <div className="px-4 py-2 rounded-2xl bg-primary/10" data-testid="alignment-subject-area">
              <p className="text-xs text-foreground/50 font-medium">Subject area</p>
              <p className="text-sm font-semibold text-primary">{a.subject_area}</p>
            </div>
          )}
        </div>

        {outcomes.length > 0 && (
          <div data-testid="alignment-outcomes">
            <h4 className="font-semibold text-secondary mb-2">What your child will learn</h4>
            <ul className="space-y-2">
              {outcomes.map((o, idx) => (
                <li key={idx} className="flex gap-2 items-start">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground/80">{o}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasNios ? (
          <div className="p-4 rounded-2xl bg-accent/10 border border-accent/20" data-testid="alignment-nios">
            <p className="text-xs font-semibold text-accent-foreground/70 uppercase tracking-wide mb-1">NIOS Curriculum Link</p>
            <p className="text-foreground/85">{a.nios_mapping}</p>
          </div>
        ) : (a.nep_2020_competency && a.nep_2020_competency.trim()) ? (
          <div className="p-4 rounded-2xl bg-accent/10 border border-accent/20" data-testid="alignment-nep">
            <p className="text-xs font-semibold text-accent-foreground/70 uppercase tracking-wide mb-1">NEP 2020 Competency</p>
            <p className="text-foreground/85">
              No direct NIOS grade-10/12 mapping - this activity builds the broader competency:{" "}
              <span className="font-semibold text-secondary">{a.nep_2020_competency}</span>
            </p>
          </div>
        ) : null}

        {a.nep_2020_competency && a.nep_2020_competency.trim() && hasNios && (
          <p className="text-sm text-foreground/60" data-testid="alignment-nep-secondary">
            Also builds the NEP 2020 competency: <span className="font-medium text-secondary">{a.nep_2020_competency}</span>
          </p>
        )}

        {ncf.length > 0 && (
          <div>
            <h4 className="font-semibold text-secondary mb-2">NCF-SE 2023 focus</h4>
            <div className="flex flex-wrap gap-2">
              {ncf.map((area, idx) => (
                <span key={idx} className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm">{area}</span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};